import { Empty } from '@arco-design/web-react';
import type { ReactNode } from 'react';

/**
 * 通知铃面板的纯展示件（`components/NotificationBell.tsx` 的内部片）：
 * 列表空态 / 列表滚动区 / 面板页脚。
 *
 * 为什么要分出来：入口文件已顶到单文件上限（`frontend.md §9`），而这里全是纯展示，
 * 与取数、WS 合并、跳转无关。本文件不被 barrel 再导出（入口的对外出口仍是
 * `NotificationBell` 默认导出），故导出面不变。
 */

/**
 * 铃铛三个页签的列表空态（统一交给 Arco `List` 的 `noDataElement`）。
 *
 * 不再在 List 外面另挂 `<Empty>`：List 在 `dataSource` 为空时**本就会渲染自带默认空态「暂无数据」**，
 * 外面再挂一个就是同一块地方两个空提示（现场即「暂无数据 + 暂无公告」上下两块）。
 * 取数中给一枚空元素而不是文案：List 在 `loading` 时照常渲染 `noDataElement`（只额外盖半透明遮罩），
 * 给了文案就成了「暂无 X + 转圈」同帧，被读成「确实没有」（口径见 components/TablePlaceholder）；
 * 这里不能返回 `null`/`undefined`——List 会回落成自带默认空态。
 */
export function listEmptyNode(loading: boolean, text: string) {
  return loading ? <span /> : <Empty description={text} style={{ padding: 24 }} />;
}

/**
 * 列表滚动区的高度上限（px）：列表在这个高度内自己滚（面板总高仍受 maxHeight 约束，不会顶穿）。
 * 与页脚配套使用——页脚留在滚动区**外面**，长列表时不必把列表拉到底才看得到出口。
 */
const PANEL_LIST_MAX_HEIGHT = 360;

/**
 * 列表滚动区：只有列表在滚，页脚（「查看全部 …→」）与空态不随列表长度被推出面板。
 *
 * 为什么不用 `position: sticky` 吸底：页脚的祖先 `.arco-tabs-content` / `.arco-tabs-content-item`
 * 自带 `overflow: hidden`，它们才是 sticky 认的滚动容器（自身不滚），吸底会退化成静态定位。
 */
export function ListScroll({ children }: { children: ReactNode }) {
  return <div style={{ maxHeight: PANEL_LIST_MAX_HEIGHT, overflow: 'auto' }}>{children}</div>;
}

/** 面板页脚行（「查看全部 …→」）：常驻面板底部，与上方滚动区用一条顶边线分隔 */
export function PanelFooter({ children }: { children: ReactNode }) {
  return (
    <div style={{ textAlign: 'right', padding: '4px 8px', borderTop: '1px solid var(--color-border-2)' }}>
      {children}
    </div>
  );
}
